import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { getDb, getAuthInstance } from '../firebase/config.js';

// ─── XP / Streak utilities (also exported for use in AppHome/FlashcardViewer) ─

export const XP_PER_CARD   = 5;
export const XP_PER_EXAM   = 50;
export const XP_STREAK_BONUS = 20;

export function getXP() {
    try { return Number(localStorage.getItem('ff:xp') || 0); } catch { return 0; }
}

export function addXP(amount) {
    try { localStorage.setItem('ff:xp', String(getXP() + amount)); } catch {}
}

export function getStreak() {
    try {
        const raw = JSON.parse(localStorage.getItem('ff:streak') || 'null');
        if (!raw) return { count: 0, lastDate: null };
        const today     = new Date().toISOString().slice(0, 10);
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        if (raw.lastDate === today || raw.lastDate === yesterday) return raw;
        return { count: 0, lastDate: null };
    } catch { return { count: 0, lastDate: null }; }
}

export function recordStudyToday() {
    try {
        const today     = new Date().toISOString().slice(0, 10);
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        const current   = getStreak();
        const newCount  = current.lastDate === today     ? current.count
                        : current.lastDate === yesterday ? current.count + 1
                        : 1;
        localStorage.setItem('ff:streak', JSON.stringify({ count: newCount, lastDate: today }));
        return newCount;
    } catch { return 0; }
}

// ─── Level system ─────────────────────────────────────────────────────────────

// Default track (shown when no onboarding profile exists)
export const LEVELS = [
    { level: 1, label: 'Preclinical',    minXP: 0     },
    { level: 2, label: 'Third Year',     minXP: 150   },
    { level: 3, label: 'Fourth Year',    minXP: 400   },
    { level: 4, label: 'Intern',         minXP: 900   },
    { level: 5, label: 'Resident',       minXP: 2000  },
    { level: 6, label: 'Fellow',         minXP: 4000  },
    { level: 7, label: 'Attending',      minXP: 7500  },
    { level: 8, label: 'Specialist',     minXP: 12000 },
    { level: 9, label: 'Board Certified',minXP: 20000 },
];

// Pathway-specific tracks — labels mirror each profession's real training arc
const TRACK_MED_STUDENT = [
    { level: 1, label: 'MS1',             minXP: 0     },
    { level: 2, label: 'Foundations',     minXP: 150   },
    { level: 3, label: 'Organ Systems',   minXP: 400   },
    { level: 4, label: 'MS3 Clerkships', minXP: 900   },
    { level: 5, label: 'Shelf Scholar',  minXP: 2000  },
    { level: 6, label: 'Step Prep',      minXP: 4000  },
    { level: 7, label: 'Sub-I',          minXP: 7500  },
    { level: 8, label: 'Match Ready',    minXP: 12000 },
    { level: 9, label: 'Board Certified',minXP: 20000 },
];

const TRACK_RESIDENT = [
    { level: 1, label: 'Intern',           minXP: 0     },
    { level: 2, label: 'PGY-2',           minXP: 150   },
    { level: 3, label: 'Senior Resident', minXP: 400   },
    { level: 4, label: 'Chief Resident',  minXP: 900   },
    { level: 5, label: 'Fellow Year 1',   minXP: 2000  },
    { level: 6, label: 'Fellow Year 2',   minXP: 4000  },
    { level: 7, label: 'Senior Fellow',   minXP: 7500  },
    { level: 8, label: 'Attending',       minXP: 12000 },
    { level: 9, label: 'Board Certified', minXP: 20000 },
];

const TRACK_NURSING = [
    { level: 1, label: 'Nursing Student',    minXP: 0     },
    { level: 2, label: 'Skills Lab',         minXP: 150   },
    { level: 3, label: 'Clinical Rotations', minXP: 400   },
    { level: 4, label: 'Senior Student',     minXP: 900   },
    { level: 5, label: 'NCLEX Prep',         minXP: 2000  },
    { level: 6, label: 'New Grad RN',        minXP: 4000  },
    { level: 7, label: 'Staff RN',           minXP: 7500  },
    { level: 8, label: 'Charge Nurse',       minXP: 12000 },
    { level: 9, label: 'NCLEX Certified',    minXP: 20000 },
];

const TRACK_NP_PA = [
    { level: 1, label: 'Program Entry',       minXP: 0     },
    { level: 2, label: 'Didactic Year 1',     minXP: 150   },
    { level: 3, label: 'Didactic Year 2',     minXP: 400   },
    { level: 4, label: 'Clinical Rotations',  minXP: 900   },
    { level: 5, label: 'Boards Prep',         minXP: 2000  },
    { level: 6, label: 'New Provider',        minXP: 4000  },
    { level: 7, label: 'Experienced Provider',minXP: 7500  },
    { level: 8, label: 'Specialist',          minXP: 12000 },
    { level: 9, label: 'Board Certified',     minXP: 20000 },
];

const TRACK_ATTENDING = [
    { level: 1, label: 'New Attending',      minXP: 0     },
    { level: 2, label: 'Junior Attending',   minXP: 150   },
    { level: 3, label: 'Clinical Attending', minXP: 400   },
    { level: 4, label: 'Experienced MD',     minXP: 900   },
    { level: 5, label: 'Clinical Expert',    minXP: 2000  },
    { level: 6, label: 'Department Lead',    minXP: 4000  },
    { level: 7, label: 'Program Director',   minXP: 7500  },
    { level: 8, label: 'Senior Specialist',  minXP: 12000 },
    { level: 9, label: 'Board Certified',    minXP: 20000 },
];

const ROLE_TRACK_MAP = {
    ms1_ms2:           TRACK_MED_STUDENT,
    ms3_ms4:           TRACK_MED_STUDENT,
    resident_fellow:   TRACK_RESIDENT,
    nursing_student:   TRACK_NURSING,
    np_pa_aa_student:  TRACK_NP_PA,
    np_pa_crna_caa:    TRACK_NP_PA,
    attending_initial: TRACK_ATTENDING,
    attending_moc:     TRACK_ATTENDING,
};

export function getLevelsForRole(role) {
    return (role && ROLE_TRACK_MAP[role]) || LEVELS;
}

export function getStoredLevels() {
    try {
        const profile = JSON.parse(localStorage.getItem('ff:onboarding:profile') || 'null');
        return getLevelsForRole(profile?.role);
    } catch { return LEVELS; }
}

export function getLevel(xp, levels = LEVELS) {
    return [...levels].reverse().find(l => xp >= l.minXP) || levels[0];
}
export function getNextLevel(xp, levels = LEVELS) {
    return levels.find(l => l.minXP > xp) || null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function tsToDate(ts) {
    if (!ts) return null;
    if (ts?.toDate) return ts.toDate();
    if (typeof ts === 'string' || typeof ts === 'number') return new Date(ts);
    return null;
}

function dateToKey(d) {
    return d ? d.toISOString().slice(0, 10) : null;
}

// ─── SVG Bar Chart ────────────────────────────────────────────────────────────

function ScoreBarChart({ exams }) {
    const W = 300, H = 110, PAD_L = 28, PAD_R = 8, PAD_T = 8, PAD_B = 22;
    const plotW = W - PAD_L - PAD_R;
    const plotH = H - PAD_T - PAD_B;
    const PASS  = 70;

    if (!exams.length) return (
        <div className="flex items-center justify-center h-28 text-slate-600 text-sm">No exams yet — take your first one!</div>
    );

    const shown = exams.slice(0, 10).reverse();
    const barW  = Math.min(22, (plotW / shown.length) - 4);

    const passY = PAD_T + plotH * (1 - PASS / 100);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
            {/* Y-axis labels */}
            {[0, 50, 70, 100].map(v => {
                const y = PAD_T + plotH * (1 - v / 100);
                return (
                    <g key={v}>
                        <text x={PAD_L - 4} y={y + 3.5} textAnchor="end" fontSize="7" fill="#475569">{v}</text>
                        <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke={v === 70 ? '#fbbf24' : '#1e293b'} strokeWidth={v === 70 ? 1 : 0.5} strokeDasharray={v === 70 ? '3 2' : undefined} />
                    </g>
                );
            })}

            {/* Bars */}
            {shown.map((e, i) => {
                const pct  = Math.max(0, Math.min(100, e.percentage ?? 0));
                const barH = plotH * (pct / 100);
                const x    = PAD_L + (plotW / shown.length) * i + (plotW / shown.length - barW) / 2;
                const y    = PAD_T + plotH - barH;
                const fill = pct >= PASS ? '#34d399' : '#f87171';
                return (
                    <g key={i}>
                        <rect x={x} y={y} width={barW} height={barH} rx="2" fill={fill} fillOpacity="0.8" />
                        <text x={x + barW / 2} y={H - PAD_B + 11} textAnchor="middle" fontSize="7" fill="#475569">{i + 1}</text>
                    </g>
                );
            })}

            {/* Pass threshold label */}
            <text x={W - PAD_R} y={passY - 2} textAnchor="end" fontSize="7" fill="#fbbf24">Pass</text>
        </svg>
    );
}

// ─── Heatmap ──────────────────────────────────────────────────────────────────

function ActivityHeatmap({ sessions }) {
    const days = Array.from({ length: 14 }, (_, i) => {
        const d = new Date(Date.now() - (13 - i) * 86400000);
        return dateToKey(d);
    });

    const byDay = {};
    for (const s of sessions) {
        const d = dateToKey(tsToDate(s.createdAt));
        if (d) byDay[d] = (byDay[d] || 0) + (s.studied || 0);
    }

    const colorFor = (n) => {
        if (!n)     return 'bg-white/5';
        if (n < 10) return 'bg-emerald-900/60';
        if (n < 30) return 'bg-emerald-600/70';
        return 'bg-emerald-400/85';
    };

    return (
        <div>
            <div className="flex items-center gap-1.5">
                {days.map(day => (
                    <div
                        key={day}
                        title={`${day}: ${byDay[day] || 0} cards`}
                        className={`flex-1 aspect-square rounded-sm ${colorFor(byDay[day] || 0)}`}
                    />
                ))}
            </div>
            <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-600">14 days ago</span>
                <span className="text-[10px] text-slate-600">Today</span>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

const PerformanceDashboard = ({ onClose, entitlements, isDevMode }) => {
    const [loading,  setLoading]  = useState(true);
    const [sessions, setSessions] = useState([]);
    const [exams,    setExams]    = useState([]);

    const xp     = getXP();
    const streak = getStreak();
    const levels = getStoredLevels();
    const level  = getLevel(xp, levels);
    const next   = getNextLevel(xp, levels);
    const levelPct = next
        ? Math.round(((xp - level.minXP) / (next.minXP - level.minXP)) * 100)
        : 100;

    useEffect(() => {
        (async () => {
            try {
                const db = getDb();
                const uid = getAuthInstance()?.currentUser?.uid;
                if (!db || !uid) { setLoading(false); return; }
                const [sessSnap, examSnap] = await Promise.all([
                    getDocs(query(collection(db, 'progress_study_sessions'), where('userId', '==', uid), orderBy('createdAt', 'desc'), limit(100))),
                    getDocs(query(collection(db, 'progress_exam_results'),   where('userId', '==', uid), orderBy('createdAt', 'desc'), limit(20))),
                ]);
                setSessions(sessSnap.docs.map(d => d.data()));
                setExams(examSnap.docs.map(d => d.data()));
            } catch (_) {
                // not logged in or no data — still show localStorage stats
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Derived stats
    const totalStudied = sessions.reduce((sum, s) => sum + (s.studied || 0), 0);

    // Weak folders: flag rate among folders with ≥5 total studied
    const folderStats = {};
    for (const s of sessions) {
        if (!s.folderName) continue;
        const f = folderStats[s.folderName] ||= { name: s.folderName, studied: 0, flagged: 0 };
        f.studied += s.studied || 0;
        f.flagged += s.flagged || 0;
    }
    const weakFolders = Object.values(folderStats)
        .filter(f => f.studied >= 5)
        .map(f => ({ ...f, rate: f.studied ? f.flagged / f.studied : 0 }))
        .sort((a, b) => b.rate - a.rate)
        .slice(0, 3);

    const recentExams = exams.slice(0, 5);

    const getScoreColor = (pct) =>
        pct >= 85 ? 'text-emerald-300' : pct >= 70 ? 'text-sky-300' : pct >= 60 ? 'text-amber-300' : 'text-rose-400';

    const getBand = (pct) =>
        pct >= 85 ? 'High Pass' : pct >= 70 ? 'Pass' : pct >= 60 ? 'Borderline' : 'Fail';

    return (
        <div className="fixed inset-0 z-40 bg-slate-950/92 backdrop-blur-md overflow-y-auto">
            <div className="mx-auto max-w-3xl px-4 py-6 pb-20">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-xl font-bold text-white tracking-tight">My Progress</h1>
                        <p className="text-xs text-slate-500 mt-0.5">Study performance & analytics</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full p-2 text-slate-400 hover:text-white hover:bg-white/10 transition"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* ── Stat cards row ── */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">

                    {/* Level / XP */}
                    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Level</p>
                        <p className="text-2xl font-bold text-white mt-0.5">{level.label}</p>
                        <p className="text-xs text-slate-400">{xp.toLocaleString()} XP</p>
                        {next && (
                            <div className="mt-2.5">
                                <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-700"
                                        style={{ width: `${levelPct}%` }}
                                    />
                                </div>
                                <p className="text-[10px] text-slate-600 mt-1">{(next.minXP - xp).toLocaleString()} XP to {next.label}</p>
                            </div>
                        )}
                        {!next && <p className="text-[10px] text-amber-400 mt-2">Max level reached 🏆</p>}
                    </div>

                    {/* Streak */}
                    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 flex flex-col items-center justify-center">
                        <p className="text-4xl leading-none">🔥</p>
                        <p className="text-3xl font-bold text-white mt-1.5 tabular-nums">{streak.count}</p>
                        <p className="text-xs text-slate-400">day streak</p>
                        {streak.count >= 7 && (
                            <span className="mt-1.5 text-[10px] font-semibold text-amber-300 bg-amber-500/10 ring-1 ring-amber-400/20 px-2 py-0.5 rounded-full">
                                On fire! +{XP_STREAK_BONUS} XP/day
                            </span>
                        )}
                    </div>

                    {/* Cards studied */}
                    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Cards Studied</p>
                        <p className="text-3xl font-bold text-white mt-0.5 tabular-nums">{totalStudied.toLocaleString()}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{exams.length} exam{exams.length !== 1 ? 's' : ''} taken</p>
                        {sessions.length > 0 && (
                            <p className="text-[10px] text-slate-600 mt-1">
                                avg {Math.round(totalStudied / Math.max(1, sessions.length))} cards/session
                            </p>
                        )}
                    </div>
                </div>

                {/* ── Exam score trend ── */}
                <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-white">Exam Score Trend</p>
                        <span className="text-[11px] text-slate-500">last {Math.min(exams.length, 10)} exams</span>
                    </div>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <span className="h-5 w-5 rounded-full border-2 border-teal-400 border-t-transparent animate-spin" />
                        </div>
                    ) : (
                        <ScoreBarChart exams={exams} />
                    )}
                    <div className="flex items-center gap-3 mt-3">
                        <span className="flex items-center gap-1 text-[11px] text-slate-500">
                            <span className="h-2 w-2 rounded-sm bg-emerald-400/80" />Pass (≥70%)
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-slate-500">
                            <span className="h-2 w-2 rounded-sm bg-rose-400/80" />Below pass
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-slate-500">
                            <span className="inline-block w-4 border-t border-dashed border-amber-400/70" />Threshold
                        </span>
                    </div>
                </div>

                {/* ── Study activity heatmap ── */}
                <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 mb-4">
                    <p className="text-sm font-semibold text-white mb-3">Study Activity — last 14 days</p>
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <span className="h-5 w-5 rounded-full border-2 border-teal-400 border-t-transparent animate-spin" />
                        </div>
                    ) : (
                        <ActivityHeatmap sessions={sessions} />
                    )}
                    <div className="flex items-center gap-2 mt-3">
                        {[['bg-white/5','0'],['bg-emerald-900/60','1–9'],['bg-emerald-600/70','10–29'],['bg-emerald-400/85','30+']].map(([cls,lbl]) => (
                            <span key={lbl} className="flex items-center gap-1 text-[10px] text-slate-500">
                                <span className={`h-2.5 w-2.5 rounded-sm ${cls}`} />{lbl}
                            </span>
                        ))}
                    </div>
                </div>

                {/* ── Two column: weak folders + recent exams ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                    {/* Weak folders */}
                    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                        <p className="text-sm font-semibold text-white mb-3">Needs Work</p>
                        {loading ? (
                            <div className="flex justify-center py-4">
                                <span className="h-4 w-4 rounded-full border-2 border-teal-400 border-t-transparent animate-spin" />
                            </div>
                        ) : weakFolders.length === 0 ? (
                            <p className="text-xs text-slate-600 py-2">Study more cards to see weak spots.</p>
                        ) : (
                            <div className="grid gap-2">
                                {weakFolders.map(f => (
                                    <div key={f.name} className="flex items-center justify-between gap-2">
                                        <span className="text-xs text-slate-300 truncate flex-1">{f.name}</span>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <div className="w-16 h-1.5 rounded-full bg-white/8 overflow-hidden">
                                                <div
                                                    className="h-full rounded-full bg-rose-400/70"
                                                    style={{ width: `${Math.round(f.rate * 100)}%` }}
                                                />
                                            </div>
                                            <span className="text-[11px] text-rose-300 tabular-nums w-8 text-right">
                                                {Math.round(f.rate * 100)}%
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Recent exam results */}
                    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                        <p className="text-sm font-semibold text-white mb-3">Recent Exams</p>
                        {loading ? (
                            <div className="flex justify-center py-4">
                                <span className="h-4 w-4 rounded-full border-2 border-teal-400 border-t-transparent animate-spin" />
                            </div>
                        ) : recentExams.length === 0 ? (
                            <p className="text-xs text-slate-600 py-2">No exams yet — take your first one!</p>
                        ) : (
                            <div className="grid gap-2">
                                {recentExams.map((e, i) => {
                                    const pct = e.percentage ?? 0;
                                    return (
                                        <div key={i} className="flex items-center justify-between gap-2">
                                            <span className="text-xs text-slate-400 truncate flex-1">
                                                {e.title || `Exam ${i + 1}`}
                                            </span>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <span className={`text-xs font-semibold tabular-nums ${getScoreColor(pct)}`}>{pct}%</span>
                                                <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${pct >= 70 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                                                    {getBand(pct)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Achievements / badges row */}
                <div className="mt-4 rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                    <p className="text-sm font-semibold text-white mb-3">Achievements</p>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { icon: '🎯', label: 'First Exam',   earned: exams.length >= 1 },
                            { icon: '📚', label: '100 Cards',    earned: totalStudied >= 100 },
                            { icon: '🔥', label: '7-Day Streak', earned: streak.count >= 7 },
                            { icon: '⭐', label: 'High Pass',    earned: exams.some(e => (e.percentage ?? 0) >= 85) },
                            { icon: '🏆', label: 'Board Master', earned: xp >= 15000 },
                            { icon: '💯', label: 'Perfect Score', earned: exams.some(e => (e.percentage ?? 0) === 100) },
                        ].map(({ icon, label, earned }) => (
                            <div
                                key={label}
                                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                                    earned
                                        ? 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/20'
                                        : 'bg-white/4 text-slate-600 ring-1 ring-white/5'
                                }`}
                            >
                                <span className={earned ? '' : 'grayscale opacity-40'}>{icon}</span>
                                {label}
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PerformanceDashboard;
